/**
 * Halos: the soft, transparent glow around the rim of every note, rail node and wall sitting on the
 * grid while the editor is paused (docs/spec/srvr/editor-shell.md SRVR-0314). The desktop editor's
 * `NotePrefabEventScript.ToggleHalos` is the model — same trigger, same tolerance — but the colour
 * comes from the object rather than Unity's white, as a first pass for the mappers to judge.
 *
 * Everything here is pure (no React, no store) so the arithmetic is unit-tested under jsdom; the
 * render paths in TunnelScene.tsx and ObstacleModel.tsx own *where* each halo is drawn, because they
 * already compute every object's transform and a second copy of that maths would drift.
 */
import * as THREE from 'three';
import { isSynthVrView } from '../../store/editorSettings';

// ── Tunables — the look is the mappers' call; change these, then the matching test values ──────

/** Unity's `Track.ON_GRID_HIGHLIGHT_TOLERANCE` (0.05 units at 0.02 units per ms). Much tighter than
 *  the half-step `gridTol` that drives the note pulse: a halo marks objects exactly on this step. */
export const HALO_TOLERANCE_MS = 2.5;
/** How far the object's colour is mixed toward white, so the glow reads as light, not paint. */
export const HALO_WHITE_MIX = 0.25;
/** The strength levels Ctrl+H cycles through after Off (SRVR-0314 AC4): the opacity at the object's
 *  outline, where the glow is brightest, and how far outside the outline it fades to nothing, as a
 *  fraction of the ring's size. Level 1 is the strength halos first shipped with. */
export const HALO_LEVELS: Readonly<Record<1 | 2 | 3, { peak: number; reach: number }>> = {
  1: { peak: 0.55, reach: 0.6 },
  2: { peak: 0.75, reach: 0.9 },
  3: { peak: 0.95, reach: 1.2 },
};
/** How far inside the outline the glow starts, as a fraction of the ring's size. */
export const HALO_INNER = 0.12;
/** The ring's geometry reaches level 3's glow; lower levels fade out sooner, in the shader. */
export const HALO_OUTER_MAX = HALO_LEVELS[3].reach;
/** A wall's ring size, as a fraction of half the shorter side of its footprint. Walls are far larger
 *  than notes, so a glow proportional to the whole wall would swamp the lane. */
export const HALO_WALL_SIZE = 0.5;
/** Halo instances per pool. Only objects on one step are drawn, so this is never approached on a
 *  real chart; past it, extra halos are skipped rather than the pool grown (as PERF-0027/0028). */
export const HALO_POOL_LIMIT = 64;

// ── The trigger ──────────────────────────────────────────────────────────────

/**
 * How close, in beats, an object must be to the playhead to be "on the beat" for the pop and the
 * un-dimming. Synth VR: the halo's 2.5 ms, however large the Step is, so only an object exactly on the
 * current beat pops and stands out. Every other view keeps the old half-step tolerance.
 */
export function onBeatTolerance(activeView: string | undefined, scrollStep: number, bpm: number | undefined): number {
  if (!isSynthVrView(activeView)) return Math.max(0.006, scrollStep * 0.55);
  return HALO_TOLERANCE_MS / (60000 / (bpm || 120));
}

/** True when an object at `objectBeat` is on the grid at `playheadBeat`: within 2.5 ms at `bpm`. */
export function isHaloOnGrid(objectBeat: number, playheadBeat: number, bpm: number): boolean {
  const msPerBeat = 60000 / (bpm || 120);
  return Math.abs(objectBeat - playheadBeat) * msPerBeat <= HALO_TOLERANCE_MS + 1e-9;
}

/** The slice of editor state that decides whether halos are shown at all. */
export interface HaloGate {
  playback: { playing: boolean };
  /** 0 = off; 1–3 index `HALO_LEVELS`. */
  highlightLevel: number;
  activeView?: string;
  beatmap?: { bpm?: number } | null;
}

/** Halos show only while paused, with a highlight level above 0, in the Synth VR editor (not Console). */
export function halosShown(s: HaloGate): boolean {
  return !s.playback.playing && s.highlightLevel > 0 && s.activeView !== 'console';
}

/** The uniforms every halo material shares, so changing the level rebuilds nothing. `uReach` is the
 *  level's reach as a fraction of `HALO_OUTER_MAX`, the distance the geometry is built out to. */
export const HALO_UNIFORMS = {
  uPeak: { value: HALO_LEVELS[1].peak },
  uReach: { value: HALO_LEVELS[1].reach / HALO_OUTER_MAX },
};

/** Point every halo at level `level`'s strength. Level 0 (off) leaves the uniforms alone — nothing
 *  is drawn then anyway — so turning halos back on resumes at a real level. */
export function applyHaloLevel(level: number): void {
  const l = HALO_LEVELS[level as 1 | 2 | 3];
  if (!l) return;
  HALO_UNIFORMS.uPeak.value = l.peak;
  HALO_UNIFORMS.uReach.value = l.reach / HALO_OUTER_MAX;
}

/** `isHaloOnGrid` at the map's BPM, for a render path that already holds the store state. Returns
 *  false whenever `halosShown` does, so callers need one test per object. */
export function haloOnGridNow(s: HaloGate, objectBeat: number, playheadBeat: number): boolean {
  return halosShown(s) && isHaloOnGrid(objectBeat, playheadBeat, s.beatmap?.bpm ?? 120);
}

// ── Colour ───────────────────────────────────────────────────────────────────

/** The halo colour for an object drawn in `hex`: that colour mixed `HALO_WHITE_MIX` toward white. */
export function haloColor(hex: string, out: THREE.Color = new THREE.Color()): THREE.Color {
  return out.set(hex).lerp(_white, HALO_WHITE_MIX);
}
const _white = new THREE.Color(1, 1, 1);

// ── Geometry ─────────────────────────────────────────────────────────────────

/** A flat ring in the XY plane around a closed convex `outline` (counter-clockwise). Each vertex
 *  carries `haloT`, where it sits across the ring: -1 at the inner edge (`HALO_INNER × size`
 *  inside), 0 on the outline and 1 at the outer edge (`HALO_OUTER_MAX × size` outside). The shader
 *  turns that into opacity for the current level. Outer corners are rounded, so a triangle's tip
 *  glows as a curve rather than a spike. */
export function buildHaloRing(outline: THREE.Vector2[], size = 1, z = 0): THREE.BufferGeometry {
  const n = outline.length;
  const din = HALO_INNER * size;
  const dout = HALO_OUTER_MAX * size;
  const pos: number[] = [];
  const across: number[] = [];
  const edgeNormal = (i: number) => {
    const a = outline[i], b = outline[(i + 1) % n];
    return new THREE.Vector2(b.y - a.y, a.x - b.x).normalize(); // outward for CCW
  };
  for (let i = 0; i < n; i++) {
    const v = outline[i];
    const nPrev = edgeNormal((i - 1 + n) % n);
    const nNext = edgeNormal(i);
    // Inner edge: the mitred inward offset, shared by every spoke at this corner.
    const bis = nPrev.clone().add(nNext).normalize();
    const miter = din / Math.max(0.25, bis.dot(nNext));
    const inner = v.clone().addScaledVector(bis, -miter);
    const turn = Math.acos(THREE.MathUtils.clamp(nPrev.dot(nNext), -1, 1));
    const spokes = Math.max(1, Math.ceil(turn / (Math.PI / 12)));
    for (let j = 0; j < spokes; j++) {
      const t = spokes === 1 ? 0.5 : j / (spokes - 1);
      const dir = spokes === 1 ? bis : slerp2(nPrev, nNext, t);
      const outer = v.clone().addScaledVector(dir, dout);
      pos.push(inner.x, inner.y, z, v.x, v.y, z, outer.x, outer.y, z);
      across.push(-1, 0, 1);
    }
  }
  const spokes = pos.length / 9;
  const index: number[] = [];
  for (let s = 0; s < spokes; s++) {
    const a = s * 3, b = ((s + 1) % spokes) * 3;
    // inner band (inner→outline) and outer band (outline→outer), two triangles each
    index.push(a, a + 1, b, b, a + 1, b + 1);
    index.push(a + 1, a + 2, b + 1, b + 1, a + 2, b + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('haloT', new THREE.Float32BufferAttribute(across, 1));
  g.setIndex(index);
  g.computeBoundingSphere();
  return g;
}

function slerp2(a: THREE.Vector2, b: THREE.Vector2, t: number): THREE.Vector2 {
  const ang = Math.atan2(a.x * b.y - a.y * b.x, a.dot(b));
  const c = Math.cos(ang * t), s = Math.sin(ang * t);
  return new THREE.Vector2(a.x * c - a.y * s, a.x * s + a.y * c);
}

/** A circle outline of radius 1 — the silhouette of the unit note and rail-node spheres. */
export function circleOutline(segments = 48): THREE.Vector2[] {
  const out: THREE.Vector2[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    out.push(new THREE.Vector2(Math.cos(a), Math.sin(a)));
  }
  return out;
}

/** The convex hull (counter-clockwise) of a geometry's footprint on the XY plane — the outline of a
 *  wall as the player faces it. Andrew's monotone chain. */
export function wallHaloOutline(geometry: THREE.BufferGeometry): THREE.Vector2[] {
  const p = geometry.getAttribute('position');
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i < p.count; i++) pts.push(new THREE.Vector2(p.getX(i), p.getY(i)));
  pts.sort((a, b) => (a.x - b.x) || (a.y - b.y));
  const cross = (o: THREE.Vector2, a: THREE.Vector2, b: THREE.Vector2) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const EPS = 1e-9;
  const lower: THREE.Vector2[] = [];
  for (const q of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= EPS) lower.pop();
    lower.push(q);
  }
  const upper: THREE.Vector2[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const q = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= EPS) upper.pop();
    upper.push(q);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

const _wallRing = new WeakMap<THREE.BufferGeometry, THREE.BufferGeometry>();
/** The halo ring for a wall shape's merged geometry, in that geometry's own local space (so the
 *  wall's own matrix places it), at the depth of the wall's middle. Cached per geometry. */
export function wallHaloGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const hit = _wallRing.get(geometry);
  if (hit) return hit;
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox!;
  const size = HALO_WALL_SIZE * Math.min(bb.max.x - bb.min.x, bb.max.y - bb.min.y) / 2;
  const ring = buildHaloRing(wallHaloOutline(geometry), size, (bb.min.z + bb.max.z) / 2);
  _wallRing.set(geometry, ring);
  return ring;
}

/** A thin flat band round a closed convex `outline` (counter-clockwise), `gap` outside it and `width`
 *  thick, in the XY plane at depth `z`. Corners are rounded like the halo's, so a triangle's tip is a
 *  curve. The selection rim: the wall counterpart of the note's SelectionRing. */
export function buildRimRing(outline: THREE.Vector2[], gap: number, width: number, z = 0): THREE.BufferGeometry {
  const n = outline.length;
  const pos: number[] = [];
  const edgeNormal = (i: number) => {
    const a = outline[i], b = outline[(i + 1) % n];
    return new THREE.Vector2(b.y - a.y, a.x - b.x).normalize(); // outward for CCW
  };
  for (let i = 0; i < n; i++) {
    const v = outline[i];
    const nPrev = edgeNormal((i - 1 + n) % n);
    const nNext = edgeNormal(i);
    const turn = Math.acos(THREE.MathUtils.clamp(nPrev.dot(nNext), -1, 1));
    const spokes = Math.max(1, Math.ceil(turn / (Math.PI / 12)));
    for (let j = 0; j < spokes; j++) {
      const t = spokes === 1 ? 0.5 : j / (spokes - 1);
      const dir = spokes === 1 ? nPrev.clone().add(nNext).normalize() : slerp2(nPrev, nNext, t);
      const a = v.clone().addScaledVector(dir, gap);
      const b = v.clone().addScaledVector(dir, gap + width);
      pos.push(a.x, a.y, z, b.x, b.y, z);
    }
  }
  const spokes = pos.length / 6;
  const index: number[] = [];
  for (let s = 0; s < spokes; s++) {
    const a = s * 2, b = ((s + 1) % spokes) * 2;
    index.push(a, a + 1, b, b, a + 1, b + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(index);
  g.computeBoundingSphere();
  return g;
}

// The selection rim is sized in WORLD units, so every wall wears the same line whatever it is scaled
// by: the squares and triangles are drawn ~5x larger than the walls, and a rim sized in the model's
// own units came out ~5x thicker on them. The note ring is 0.014 world thick; these are trimmed by eye.
export const WALL_RIM_WIDTH = 0.008;
export const WALL_RIM_GAP = 0.002;

const _wallRim = new WeakMap<THREE.BufferGeometry, Map<number, THREE.BufferGeometry>>();
/** The selection rim for a wall shape's merged geometry, in that geometry's own local space so the
 *  wall's own matrix places it. `worldScale` is how many world units one unit of the geometry is
 *  drawn as (the wall's scale, obstacle scale included); the line is that thin in the world. */
export function wallRimGeometry(geometry: THREE.BufferGeometry, worldScale: number): THREE.BufferGeometry {
  const key = Math.round(Math.abs(worldScale) * 1e6);
  let bySize = _wallRim.get(geometry);
  if (!bySize) _wallRim.set(geometry, bySize = new Map());
  const hit = bySize.get(key);
  if (hit) return hit;
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox!;
  const k = 1 / Math.max(1e-6, Math.abs(worldScale));
  const rim = buildRimRing(wallHaloOutline(geometry), WALL_RIM_GAP * k, WALL_RIM_WIDTH * k, (bb.min.z + bb.max.z) / 2);
  bySize.set(key, rim);
  return rim;
}

/** The white of the rim, shared by every selected wall; drawn over the scene like the note ring. */
export const WALL_RIM_MATERIAL = new THREE.MeshBasicMaterial({
  color: '#FFFFFF', transparent: true, opacity: 0.95, side: THREE.DoubleSide,
  toneMapped: false, depthTest: false, depthWrite: false,
});

let _noteRing: THREE.BufferGeometry | null = null;
/** The halo ring around a unit sphere, shared by every note and rail-node halo. */
export function noteHaloGeometry(): THREE.BufferGeometry {
  return (_noteRing ??= buildHaloRing(circleOutline()));
}

// ── Material ─────────────────────────────────────────────────────────────────

const VERT = /* glsl */ `
attribute float haloT;
uniform vec3 uColor;
varying float vT;
varying vec3 vColor;
void main() {
  vT = haloT;
  #ifdef USE_INSTANCING_COLOR
    vColor = instanceColor;
  #else
    vColor = uColor;
  #endif
  #ifdef USE_INSTANCING
    mat4 mv = modelViewMatrix * instanceMatrix;
  #else
    mat4 mv = modelViewMatrix;
  #endif
  #ifdef HALO_BILLBOARD
    // Face the camera: the ring is a silhouette outline, so it is laid out in view space around the
    // object's centre at the object's own scale.
    float s = length(mv[0].xyz);
    vec4 p = mv * vec4(0.0, 0.0, 0.0, 1.0);
    p.xy += position.xy * s;
    gl_Position = projectionMatrix * p;
  #else
    gl_Position = projectionMatrix * mv * vec4(position, 1.0);
  #endif
}`;

const FRAG = /* glsl */ `
uniform float uPeak;
uniform float uReach;
varying float vT;
varying vec3 vColor;
void main() {
  // Quadratic fall-off either side of the outline: bright at the rim, soft at the edges. Inside, it
  // fades over the whole inner band; outside, over the level's reach (a fraction of the band).
  float f = vT < 0.0 ? 1.0 + vT : 1.0 - vT / uReach;
  f = clamp(f, 0.0, 1.0);
  float a = uPeak * f * f;
  gl_FragColor = vec4(vColor, a);
}`;

let _noteHaloMat: THREE.ShaderMaterial | null = null;
/** The one material every note and rail-node halo pool shares: billboarded, coloured per instance. */
export function noteHaloMaterial(): THREE.ShaderMaterial {
  return (_noteHaloMat ??= makeHaloMaterial({ billboard: true }));
}

/** Drawn after the rails (renderOrder 1) and the walls, so a halo blends over both. */
export const HALO_RENDER_ORDER = 2;

/**
 * A companion pool of halo instances for one object pool. Its writer calls `begin()`, then `add()`
 * with each on-grid object's own instance matrix (so the halo sits exactly where the object is
 * drawn), then `end()`. An empty pool is hidden, so a frame with nothing on the grid draws nothing
 * extra. Never raycast: a halo must not take a click meant for the object under it.
 */
export class HaloPool {
  readonly mesh: THREE.InstancedMesh;
  private n = 0;

  constructor(geometry: THREE.BufferGeometry, material: THREE.Material, name: string, perInstanceColor: boolean, kind: HaloKind) {
    const mesh = new THREE.InstancedMesh(geometry, material, HALO_POOL_LIMIT);
    mesh.name = name;
    mesh.userData.halo = kind;
    mesh.frustumCulled = false;
    mesh.renderOrder = HALO_RENDER_ORDER;
    mesh.raycast = () => undefined;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (perInstanceColor) {
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(HALO_POOL_LIMIT * 3), 3);
      mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    }
    mesh.count = 0;
    mesh.visible = false;
    this.mesh = mesh;
  }

  begin(): void { this.n = 0; }

  /** Adds one halo; past `HALO_POOL_LIMIT` it is skipped, never grown. */
  add(matrix: THREE.Matrix4, color?: THREE.Color): void {
    if (this.n >= HALO_POOL_LIMIT) return;
    this.mesh.setMatrixAt(this.n, matrix);
    if (color && this.mesh.instanceColor) this.mesh.setColorAt(this.n, color);
    this.n++;
  }

  end(): void {
    const m = this.mesh;
    m.count = this.n;
    m.visible = this.n > 0;
    if (this.n > 0) {
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
  }

  /** Releases the instance buffers; the geometry and material are shared and not this pool's. */
  dispose(): void { this.mesh.dispose(); }
}

/** What a halo belongs to, so the scene can be inspected by kind (the harness does). */
export type HaloKind = 'note' | 'railNode' | 'wall' | 'light' | 'bookmark';

/** A single halo for an object drawn as its own mesh (selected, dragged, or a rail node). Added as
 *  that mesh's child, so it inherits the object's transform; its owner sets `visible` each frame. */
export function makeHaloMesh(geometry: THREE.BufferGeometry, billboard: boolean, color: THREE.ColorRepresentation, kind: HaloKind): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, makeHaloMaterial({ billboard, color }));
  mesh.name = `halo-${kind}`;
  mesh.userData.halo = kind;
  mesh.renderOrder = HALO_RENDER_ORDER;
  mesh.raycast = () => undefined;
  mesh.visible = false;
  return mesh;
}

/** The halo material. Transparent, tests depth (an object in front hides a halo behind it) but never
 *  writes it (a halo hides nothing). Colour is per instance on a pool, `color` on a single mesh.
 *  `billboard` turns the ring to face the camera — for the spheres; walls lie in their own plane. */
export function makeHaloMaterial({ billboard, color }: { billboard: boolean; color?: THREE.ColorRepresentation }): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: { uColor: { value: new THREE.Color(color ?? _white) }, uPeak: HALO_UNIFORMS.uPeak, uReach: HALO_UNIFORMS.uReach },
    defines: billboard ? { HALO_BILLBOARD: '' } : {},
    transparent: true,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.NormalBlending,
    toneMapped: false,
    fog: false,
  });
}
